import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { formatDate } from '@/lib/dateFormat';
import { idManagementService, type MyAccountId } from '@/services/id-management.service';
import { CategoryIcon, CategoryLabel, CategoryOption } from '@/components/id-management/CategoryIcon';
import { IdCardList } from '@/components/id-management/IdCardList';
import { SortHeader } from '@/components/id-management/SortHeader';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_SORT = 'category:asc';
const ALL = '__all__';

/** Cards have no column headers to click, so they sort from this list instead. */
const CARD_SORT_OPTIONS = [
  { value: DEFAULT_SORT, label: 'Category A–Z' },
  { value: 'account_identifier:asc', label: 'Account ID A–Z' },
  { value: 'updated_at:desc', label: 'Recently updated' },
];

export function MyIdsPage() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const sort = searchParams.get('sort') ?? DEFAULT_SORT;
  const categoryId = searchParams.get('category_id') ?? '';
  const q = searchParams.get('q') ?? '';

  const updateParams = (changes: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const [search, setSearch] = useState(q);
  useEffect(() => {
    if (search === q) return undefined;
    const timer = setTimeout(() => updateParams({ q: search || null, page: null }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // updateParams is rebuilt each render; only the typed text should restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, q]);

  const idsQuery = useQuery({
    queryKey: ['id-management', 'my', { page, sort, categoryId, q }],
    queryFn: () => idManagementService.listMine({
      page,
      limit: PAGE_SIZE,
      sort,
      q: q || undefined,
      category_id: categoryId || undefined,
    }),
  });

  const categoriesQuery = useQuery({
    queryKey: ['id-management', 'categories'],
    queryFn: () => idManagementService.listCategories(),
  });

  const sortHeader = (field: string, label: string) => (
    <SortHeader field={field} label={label} sort={sort} onSort={(s) => updateParams({ sort: s, page: null })} />
  );

  const columns: Column<MyAccountId>[] = [
    {
      key: 'category',
      header: sortHeader('category', 'Category'),
      cell: (row) => <CategoryLabel name={row.category_name} iconPath={row.category_icon_path} />,
    },
    {
      key: 'account_identifier',
      header: sortHeader('account_identifier', 'Account ID'),
      className: 'min-w-[200px]',
      cell: (row) => <span className="break-all font-medium text-foreground">{row.account_identifier}</span>,
    },
    {
      key: 'updated_at',
      header: sortHeader('updated_at', 'Updated'),
      cell: (row) => <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.updated_at)}</span>,
    },
  ];

  const pagination = idsQuery.data
    ? {
        page: idsQuery.data.pagination.page,
        totalPages: idsQuery.data.pagination.totalPages,
        total: idsQuery.data.pagination.total,
        onPageChange: (p: number) => updateParams({ page: String(p) }),
      }
    : undefined;

  const hasFilters = Boolean(q || categoryId);
  const emptyMessage = hasFilters
    ? 'No IDs match these filters.'
    : 'No IDs have been saved for you yet. Your admin adds them here when they set up your accounts.';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/account')}
          className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-muted"
          aria-label="Back to account"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <KeyRound className="h-5 w-5 text-teal-600" />
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">My IDs</h1>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search category or account ID…"
          containerClassName="col-span-2 w-full sm:w-72"
        />
        <Select
          value={categoryId || ALL}
          onValueChange={(v) => updateParams({ category_id: v === ALL ? null : v, page: null })}
        >
          <SelectTrigger className="h-9 w-full sm:w-48">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {(categoriesQuery.data ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}><CategoryOption name={c.name} iconPath={c.icon_path} /></SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-full sm:w-48 md:hidden">
          <Select value={sort} onValueChange={(v) => updateParams({ sort: v, page: null })}>
            <SelectTrigger className="h-9 w-full" aria-label="Sort">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {CARD_SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hidden md:block">
        <DataTable
          columns={columns}
          rows={idsQuery.data?.data ?? []}
          rowKey={(row) => row.id}
          loading={idsQuery.isLoading}
          loadingRows={4}
          error={idsQuery.isError}
          onRetry={() => void idsQuery.refetch()}
          empty={emptyMessage}
          pagination={pagination}
        />
      </div>
      <IdCardList
        className="md:hidden"
        rows={idsQuery.data?.data ?? []}
        rowKey={(row) => row.id}
        loading={idsQuery.isLoading}
        error={idsQuery.isError}
        onRetry={() => void idsQuery.refetch()}
        empty={emptyMessage}
        pagination={pagination}
        renderCard={(row) => (
          <article className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-card p-3">
            <CategoryIcon name={row.category_name} iconPath={row.category_icon_path} className="h-9 w-9 rounded-lg" letterClassName="text-sm" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight text-foreground [overflow-wrap:anywhere]">{row.account_identifier}</p>
              <p className="flex justify-between gap-2 text-xs text-muted-foreground">
                <span className="min-w-0 [overflow-wrap:anywhere]">{row.category_name}</span>
                <span className="shrink-0">{formatDate(row.updated_at)}</span>
              </p>
            </div>
          </article>
        )}
      />
    </div>
  );
}
